// Copyright (c) 2021 WiseTime. All rights reserved.

import {
  useEffect,
  useState,
} from "react"
import { findLastIndex } from "ramda"
import {
  ListItemEitherType,
  ListItemFavouriteType,
} from "./types"

export const useDropdownContentHandling = () => {
  const [activeIdIndex, setActiveIdIndex] = useState<number | undefined>(undefined)
  const [activeId, setActiveId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (activeId !== undefined) {
      document.getElementById(activeId)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      })
    }
  }, [activeId])

  const handleArrowNavigation = (
    arrow: "ArrowUp" | "ArrowDown" | "ArrowRightOrLeft",
    list: ListItemEitherType[],
    addedItemIds: string[] = [],
  ): void => {
    const getActiveIdIndex = (): number | undefined => {
      let nextIndex = activeIdIndex
      const isNotPresent = (listItemId: string): boolean => !addedItemIds.some(id => id === listItemId)
      const isFavouriteListItem = (listItem: unknown): listItem is ListItemFavouriteType => {
        if (!listItem) {
          return false
        }
        const item = listItem as ListItemFavouriteType
        return "refId" in item
      }
      const getRelevantId = (listItem: ListItemEitherType): string =>
        isFavouriteListItem(listItem) ? listItem.refId : listItem.id
      const getArrowDownNextIndex = (): number | undefined => {
        if (activeIdIndex === undefined) {
          return list.findIndex((listItem: ListItemEitherType, index: number): boolean => (
            index >= (activeIdIndex || 0) && isNotPresent(getRelevantId(listItem))
          ))
        }
        if (activeIdIndex < list.length - 1) {
          return list.findIndex((listItem: ListItemEitherType, index: number): boolean => (
            index > (activeIdIndex || 0) && isNotPresent(getRelevantId(listItem))
          ))
        }
        return nextIndex
      }

      if (arrow === "ArrowDown") {
        nextIndex = getArrowDownNextIndex()
      }

      if (arrow === "ArrowUp" && activeIdIndex !== undefined
        && activeIdIndex > 0 && activeIdIndex < list.length) {
        nextIndex = findLastIndex(
          (listItem: ListItemEitherType): boolean => (
            list.indexOf(listItem) < (activeIdIndex || 0) && isNotPresent(getRelevantId(listItem))
          ),
          list,
        )
      }

      if (arrow === "ArrowRightOrLeft") {
        nextIndex = list.findIndex((listItem: ListItemEitherType, index: number): boolean => (
          index >= 0 && isNotPresent(getRelevantId(listItem))
        ))
      }

      return nextIndex === -1 ? activeIdIndex : nextIndex
    }

    const index = getActiveIdIndex()
    setActiveIdIndex(index)
    setActiveId(index !== undefined ? list[index]?.id : index)
  }
  return {
    activeIdIndex,
    setActiveIdIndex,
    activeId,
    setActiveId,
    handleArrowNavigation,
  }
}
